package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.appScope
import com.steamstreet.vegasful.browser.spinnerSvg
import com.steamstreet.vegasful.css.css
import com.steamstreet.vegasful.web.SEARCH_RESULT_COUNT_ATTRIBUTE
import com.steamstreet.vegasful.web.analytics.Analytics
import com.steamstreet.vegasful.web.analytics.AnalyticsEvent
import com.steamstreet.vegasful.web.analytics.AnalyticsParam
import com.steamstreet.vegasful.web.analytics.entityIdOf
import com.steamstreet.vegasful.web.analytics.entityTypeOf
import com.steamstreet.vegasful.web.analytics.pageTypeOf
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.css.em
import kotlinx.css.fontSize
import kotlinx.html.dom.append
import kotlinx.html.js.span
import kotlinx.html.unsafe
import org.w3c.dom.Element
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.asList
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.url.URL
import kotlin.time.Duration.Companion.milliseconds

/**
 * Initialize the global search
 */
fun globalSearch(element: Element) {
    val input = (element.querySelector("input") as? HTMLInputElement)
    fun navigateToSearch(text: String) {
        // The query itself is never reported — only its length. A search box is somewhere people
        // type things about themselves, and `docs/analytics-plan.md` keeps free text out of the
        // analytics stream on purpose. `results_count` is unknown here; the results page reports
        // its own `search_performed` once it knows.
        Analytics.track(
            AnalyticsEvent.SEARCH_PERFORMED,
            AnalyticsParam.QUERY_LENGTH to text.length,
            AnalyticsParam.SEARCH_TYPE to SEARCH_TYPE_GLOBAL,
            AnalyticsParam.PAGE_TYPE to pageTypeOf(window.location.pathname)
        )
        document.location?.href = "${document.location?.origin}/search?searchText=${encodeURIComponent(text)}"
    }

    // Render the suggestion dropdown as a direct child of <body> (a "portal") so it escapes any
    // ancestor stacking context or overflow clipping and reliably floats above page content.
    // Its position is derived from the input's viewport rect each time it is shown.
    val dropdown = (document.createElement("div") as HTMLElement).apply {
        setAttribute("style", "position:fixed;z-index:2147483000;display:none;")
    }
    document.body?.appendChild(dropdown)

    // A suggestion being followed is the outcome of the typeahead, and the only part of it worth
    // an event: reporting a `search_performed` per keystroke would bury the real searches.
    dropdown.addEventListener("click", { event ->
        val row = (event.target as? Element)?.closest(".typeahead-row") as? HTMLAnchorElement
        if (row != null) {
            val rows = dropdown.querySelectorAll(".typeahead-row").asList()
            Analytics.track(
                AnalyticsEvent.SEARCH_RESULT_TAPPED,
                AnalyticsParam.ENTITY_TYPE to entityTypeOf(row.pathname),
                AnalyticsParam.ENTITY_ID to entityIdOf(row.pathname),
                AnalyticsParam.POSITION to rows.indexOf(row),
                AnalyticsParam.SEARCH_TYPE to SEARCH_TYPE_TYPEAHEAD
            )
        }
    })

    fun positionDropdown() {
        val rect = (input ?: return).getBoundingClientRect()
        dropdown.style.left = "${rect.left}px"
        dropdown.style.top = "${rect.bottom}px"
        dropdown.style.width = "${rect.width}px"
    }

    fun hideDropdown() {
        dropdown.style.display = "none"
    }

    fun showDropdown() {
        if (dropdown.querySelector(".typeahead-row") != null) {
            positionDropdown()
            dropdown.style.display = "block"
        }
    }

    // Debounce keystrokes so we hit the typeahead endpoint at most once per pause in typing.
    var searchJob: Job? = null
    fun scheduleTypeahead(text: String) {
        searchJob?.cancel()
        if (text.length < 2) {
            dropdown.innerHTML = ""
            hideDropdown()
            return
        }
        searchJob = appScope.launch {
            delay(250.milliseconds)
            val html = Account.load("/search/typeahead?searchText=${encodeURIComponent(text)}")
            dropdown.innerHTML = html ?: ""
            // The snippet always comes back as a full document; only show the dropdown when it
            // actually contains suggestion rows, otherwise it would render as an empty box.
            if (dropdown.querySelector(".typeahead-row") != null) {
                showDropdown()
            } else {
                dropdown.innerHTML = ""
                hideDropdown()
            }
        }
    }

    input?.addEventListener("input", {
        scheduleTypeahead((it.target as? HTMLInputElement)?.value.orEmpty())
    })

    input?.addEventListener("keydown", { event ->
        val value = (event.target as? HTMLInputElement)?.value
        when ((event as KeyboardEvent).keyCode) {
            13 -> if (!value.isNullOrBlank()) navigateToSearch(value)  // Enter -> full results page
            27 -> hideDropdown()  // Escape
        }
    })

    input?.addEventListener("focus", { showDropdown() })
    // Delay the hide so a click on a suggestion registers before the dropdown is removed.
    input?.addEventListener("blur", {
        appScope.launch {
            delay(150.milliseconds)
            hideDropdown()
        }
    })

    // Keep the portalled dropdown aligned with the input as the page scrolls or resizes.
    val reposition: (org.w3c.dom.events.Event) -> Unit = {
        if (dropdown.style.display == "block") {
            positionDropdown()
        }
    }
    window.addEventListener("scroll", reposition, true)
    window.addEventListener("resize", reposition)

    element.querySelector("span")?.addEventListener("click", {
        input?.value?.takeIf { it.isNotBlank() }?.let {
            navigateToSearch(it)
        }
    })

    val searchText = document.location?.href?.let { URL(it) }?.searchParams?.get("searchText")
    if (searchText != null) {
        input?.value = searchText
    }

    input?.focus()
}

/**
 * Executes a search and sets the results
 */
fun searchResults(element: Element) {
    val query = element.getAttribute("data-search-input")
    if (!query.isNullOrBlank()) {
        appScope.launch {
            val resultsContainer = element.querySelector(".search-results-container")
            var done = false

            launch(Dispatchers.Main) {
                delay(700.milliseconds)
                if (!done) {
                    resultsContainer?.append {
                        span {
                            css {
                                fontSize = 2.em
                            }
                            unsafe {
                                +spinnerSvg()
                            }
                        }
                    }
                }
            }

            val result = Account.load("/search/results?searchText=${encodeURIComponent(query)}")
            done = true
            if (result != null) {
                resultsContainer?.let {
                    it.innerHTML = result
                }
            }

            // Reported once the results are in, so that `results_count` is a fact rather than a
            // guess — the server writes the count onto the fragment it rendered. A failed load
            // reports the search too, with no count, since a search that errored still happened.
            Analytics.track(
                AnalyticsEvent.SEARCH_PERFORMED,
                AnalyticsParam.QUERY_LENGTH to query.length,
                AnalyticsParam.SEARCH_TYPE to SEARCH_TYPE_RESULTS,
                AnalyticsParam.RESULTS_COUNT to resultCount(resultsContainer)
            )
            if (result == null) {
                Analytics.apiError(SEARCH_TYPE_RESULTS, "search results did not load")
            }

            // A card on the results page is the same event as a suggestion in the dropdown, told
            // apart by `search_type`.
            resultsContainer?.addEventListener("click", { event ->
                val anchor = (event.target as? Element)?.closest("a[href]") as? HTMLAnchorElement
                val type = anchor?.pathname?.let { entityTypeOf(it) }
                if (anchor != null && type != null && type != "unknown") {
                    Analytics.track(
                        AnalyticsEvent.SEARCH_RESULT_TAPPED,
                        AnalyticsParam.ENTITY_TYPE to type,
                        AnalyticsParam.ENTITY_ID to entityIdOf(anchor.pathname),
                        AnalyticsParam.SEARCH_TYPE to SEARCH_TYPE_RESULTS
                    )
                }
            })
        }
    }
}

/** How many results the server said the injected fragment holds, or null if it did not say. */
private fun resultCount(container: Element?): Int? =
    container?.querySelector("[$SEARCH_RESULT_COUNT_ATTRIBUTE]")
        ?.getAttribute(SEARCH_RESULT_COUNT_ATTRIBUTE)?.toIntOrNull()

/** The search box in the site header, which navigates to the results page. */
private const val SEARCH_TYPE_GLOBAL = "global"

/** The as-you-type suggestion dropdown. */
private const val SEARCH_TYPE_TYPEAHEAD = "typeahead"

/** The full results page. */
private const val SEARCH_TYPE_RESULTS = "results"